const BigNumber = require('bignumber.js');
const FieldLevel = require('@/lib/CustomFields/FieldLevel');
const { cloneDeep } = require('@/functions/clone');

/**
 * Return all FieldGroups from the array that match the criteria.
 * All of the criteria will determine if a FieldGroup is returned. The following will also filter the instances
 * within the groups that are returned:
 * - askEarly
 * - hideNoValue
 * - showToCustomers
 *
 * @param {FieldGroup[]} fieldGroups
 * @param {{hideNoValue?: boolean, level?: string, itemInstance?: number, lineNumber?: number, askEarly?: boolean,
 *     showToCustomers?: boolean}} filterOptions
 *
 * @return {FieldGroup[]}
 */
const filterFieldGroups = (fieldGroups, filterOptions) => {
    const validGroups = [];

    /**
     * Is the attribute present in the fieldoptions and is it appropriately defined.
     *
     * @param {string} attribute
     * @return {boolean}
     */
    function isset(attribute) {
        return filterOptions.hasOwnProperty(attribute) && filterOptions[attribute] !== undefined;
    }

    for (let i = 0, j = fieldGroups.length; i < j; ++i) {
        /**
         * Clone the FieldGroup so we don't modify the original, because we are filtering the instances within it.
         *
         * @type {FieldGroup} fieldGroup
         */
        let fieldGroup = cloneDeep(fieldGroups[i]);

        if (isset('level') && (filterOptions.level !== fieldGroup.level)) {
            continue;
        }

        if (isset('lineNumber') && (filterOptions.lineNumber !== fieldGroup.lineNumber)) {
            continue;
        }

        if (isset('itemInstance') && (filterOptions.itemInstance !== fieldGroup.itemInstance)) {
            continue;
        }

        if (isset('hideNoValue') && filterOptions.hideNoValue && !fieldGroup.hasInstancesWithValue()) {
            continue;
        }

        if (isset('askEarly')) {
            if (filterOptions.askEarly) {
                // Return only askEarly fields.
                fieldGroup.instances = fieldGroup.instances.filter((fieldInstance) => {
                    return !!fieldInstance.field.askEarly;
                });
            } else {
                // Return only non-askEarly fields.
                fieldGroup.instances = fieldGroup.instances.filter((fieldInstance) => {
                    return !fieldInstance.field.askEarly;
                });
            }
            if (fieldGroup.instances.length < 1) {
                continue;
            }
        }

        if (isset('hideNoValue') && filterOptions.hideNoValue) {
            fieldGroup.instances = fieldGroup.instances.filter((fieldInstance) => {
                return fieldInstance.displayValue && fieldInstance.displayValue.length > 0;
            });
            if (fieldGroup.instances.length < 1) {
                continue;
            }
        }

        if (isset('showToCustomers')) {
            fieldGroup.instances = fieldGroup.instances.filter((fieldInstance) => {
                return fieldInstance.field.showToCustomers === filterOptions.showToCustomers;
            });
            if (fieldGroup.instances.length < 1) {
                continue;
            }
        }

        validGroups.push(fieldGroup);
    }

    return validGroups;
};

/**
 * Accepts a FieldGroupCollection and returns an array of all the "ask early" (product option)
 * field instances, organised by line number.
 *
 * Note that for each line it only includes ask early field instances from the first item instance,
 * because all ask early values should be the same for every item instance on the same line.
 *
 * @param {FieldGroupCollection} fieldGroupCollection
 * @param {boolean|null} [showToCustomers]
 */
const getAskEarlyFieldInstancesByLine = (fieldGroupCollection, showToCustomers = null) => {
    const lineAskEarlyInstances = {};

    fieldGroupCollection.groups.forEach((fieldGroup) => {
        if (
            fieldGroup.level !== FieldLevel.ITEM
            || fieldGroup.instances.length < 1
            || fieldGroup.itemInstance > 0 // Only include field instances for the first item instance.
        ) {
            return;
        }

        if (!lineAskEarlyInstances.hasOwnProperty(fieldGroup.lineNumber)) {
            lineAskEarlyInstances[fieldGroup.lineNumber] = [];
        }

        const lineInstances = fieldGroup.instances.filter((fieldInstance) => {
            if (showToCustomers !== null) {
                if (fieldInstance.field.showToCustomers !== showToCustomers) {
                    return false;
                }
            }

            return !!fieldInstance.field.askEarly && fieldInstance.hasValue;
        });

        // Sort the field instances so that those with a price are displayed first.
        lineAskEarlyInstances[fieldGroup.lineNumber] = sortInstancesByPrice(lineInstances);
    });

    return lineAskEarlyInstances;
};

/**
 * Sort the array of instances so that instances where priceForSelectedOptions = 0 are displayed last.
 *
 * @param {FieldInstance[]} lineInstances
 *
 * @return {FieldInstance[]}
 */
const sortInstancesByPrice = (lineInstances) => {
    return lineInstances.sort((a, b) => {
        if (a.priceForSelectedOptions === 0 && b.priceForSelectedOptions === 0) {
            return 0; // Both prices = 0 so don't change the order.
        }
        if (a.priceForSelectedOptions === 0) {
            return 1; // a  = 0 so move to the end.
        }
        if (b.priceForSelectedOptions === 0) {
            return -1; // b = 0 so move to the end.
        }
        // If the instances both have prices leave the order unchanged. Some clients may care about the order of
        // the custom fields so let's touch them as little as possible.
        return 0;
    });
};

/**
 * @param {CartLine|OrderLine} line
 *
 * @return {number}
 */
const getPriceForOptionsOnLine = (line) => {
    let total = new BigNumber(0);

    // Sum the price of all chargeable product options.
    // Ask-early field values should be the same for every instance on the same line, so we only look at
    // the fields for the first instance on the line.
    const firstItemInstance = line.getInstance(1);
    if (firstItemInstance) {
        firstItemInstance.fields.instances.forEach((fieldInstance) => {
            total = total.plus(fieldInstance.priceForSelectedOptions);
        });
    }

    return total.toNumber();
};

module.exports = {
    filterFieldGroups,
    getAskEarlyFieldInstancesByLine,
    getPriceForOptionsOnLine,
    sortInstancesByPrice,
};
